// src/components/AgentCore/AgentCoreView.jsx
// AgentCore Observability — multi-cloud agent fleet monitoring, run tracing, tool analytics
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  Bot, Cpu, Zap, Clock, CheckCircle, XCircle, Activity, Layers,
  GitBranch, Database, Search, RefreshCw, ChevronRight,
  ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Hash,
  Globe, Cloud, Server, Leaf, Brain, Wrench,
  Network, Shield, Play, Pause, ArrowRight,
  FileCode2, MessageSquare, Settings2, Star, Info, X,
  DollarSign,
} from 'lucide-react';

// ─── Provider config ─────────────────────────────────────────────────────────
const PROVIDERS = {
  bedrock: { label:'AWS Bedrock',  color:'#f97316', icon:'🟠', bg:'rgba(249,115,22,.12)', border:'rgba(249,115,22,.3)' },
  azure:   { label:'Azure AI',     color:'#0078d4', icon:'🔵', bg:'rgba(0,120,212,.12)',  border:'rgba(0,120,212,.3)'  },
  vertex:  { label:'Vertex AI',    color:'#4285f4', icon:'🟣', bg:'rgba(66,133,244,.12)', border:'rgba(66,133,244,.3)' },
  openai:  { label:'OpenAI',       color:'#10a37f', icon:'🟢', bg:'rgba(16,163,127,.12)', border:'rgba(16,163,127,.3)' },
};

const STEP_COLORS = {
  PRE_PROCESSING:         '#60a5fa',
  ORCHESTRATION:          '#a78bfa',
  KNOWLEDGE_BASE_LOOKUP:  '#34d399',
  ACTION_GROUP:           '#fbbf24',
  POST_PROCESSING:        '#60a5fa',
  message_creation:       '#a78bfa',
  tool_calls:             '#fbbf24',
  TOOL_USE:               '#fbbf24',
  USER_INPUT:             '#60a5fa',
  AGENT_RESPONSE:         '#34d399',
};

const STATUS_CFG = {
  SUCCEEDED:        { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Succeeded'   },
  completed:        { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Completed'   },
  END_OF_SESSION:   { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Completed'   },
  FAILED:           { color:'#ef4444', bg:'rgba(239,68,68,.12)',    label:'Failed'      },
  failed:           { color:'#ef4444', bg:'rgba(239,68,68,.12)',    label:'Failed'      },
  IN_PROGRESS:      { color:'#f59e0b', bg:'rgba(245,158,11,.12)',   label:'Running'     },
  in_progress:      { color:'#f59e0b', bg:'rgba(245,158,11,.12)',   label:'Running'     },
  RUNNING:          { color:'#f59e0b', bg:'rgba(245,158,11,.12)',   label:'Running'     },
  CREATING:         { color:'#8b5cf6', bg:'rgba(139,92,246,.12)',   label:'Deploying'   },
  PREPARED:         { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Ready'       },
  active:           { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Active'      },
  ACTIVE:           { color:'#22c55e', bg:'rgba(34,197,94,.12)',    label:'Active'      },
};

// ─── Demo data (used when API is unavailable) ─────────────────────────────────
const rnd  = (a, b) => Math.random() * (b - a) + a;
const rndI = (a, b) => Math.floor(rnd(a, b));
const pick  = arr    => arr[rndI(0, arr.length)];

const DEMO_AGENTS = [
  // Bedrock
  { agentId:'AGNT1', name:'enterprise-support-agent',  provider:'bedrock', model:'claude-3-5-sonnet', status:'PREPARED', region:'us-east-1', description:'Customer support with CRM/ticketing integration', tools:[{name:'CRM_Lookup'},{name:'Ticket_Manager'},{name:'KB_Search'}], memoryEnabled:true, _demo:true },
  { agentId:'AGNT2', name:'data-analyst-agent',         provider:'bedrock', model:'claude-3-haiku',    status:'PREPARED', region:'us-east-1', description:'Automated data analysis from Redshift + S3', tools:[{name:'Redshift_Query'},{name:'S3_Reader'},{name:'Chart_Generator'}], memoryEnabled:false, _demo:true },
  { agentId:'AGNT3', name:'security-triage-agent',      provider:'bedrock', model:'titan-premier',     status:'CREATING', region:'us-west-2', description:'GuardDuty finding analysis and IAM triage', tools:[{name:'GuardDuty_Lookup'},{name:'IAM_Analyzer'}], memoryEnabled:true, _demo:true },
  // Azure
  { agentId:'AGNT4', name:'azure-copilot-enterprise',  provider:'azure',   model:'gpt-4o',      status:'active', region:'eastus',   description:'M365 Copilot with SharePoint/Teams integration', tools:[{name:'get_sharepoint_doc'},{name:'search_teams_messages'},{name:'code_interpreter'},{name:'file_search'}], _demo:true },
  { agentId:'AGNT5', name:'azure-data-intelligence',   provider:'azure',   model:'gpt-4o-mini', status:'active', region:'westus2',  description:'BI reporting from Synapse and Power BI', tools:[{name:'run_synapse_query'},{name:'get_powerbi_report'},{name:'code_interpreter'}], _demo:true },
  { agentId:'AGNT6', name:'azure-devops-agent',        provider:'azure',   model:'gpt-4o',      status:'active', region:'eastus',   description:'CI/CD pipeline automation and work item management', tools:[{name:'list_pipelines'},{name:'trigger_deployment'},{name:'get_work_items'}], _demo:true },
  // Vertex
  { agentId:'AGNT7', name:'vertex-research-agent',     provider:'vertex',  model:'gemini-1.5-pro', status:'ACTIVE', region:'us-central1', description:'Deep research with Google Search and Scholar', tools:[{name:'google_search'},{name:'code_execution'},{name:'internal_docs_search'}], _demo:true },
  { agentId:'AGNT8', name:'vertex-ops-agent',          provider:'vertex',  model:'gemini-1.5-flash', status:'ACTIVE', region:'us-central1', description:'GKE and Cloud Run operations management', tools:[{name:'gke_api'},{name:'cloud_monitoring'},{name:'bigquery_tool'}], _demo:true },
  { agentId:'AGNT9', name:'vertex-customer-agent',     provider:'vertex',  model:'gemini-1.0-pro', status:'ACTIVE', region:'europe-west1', description:'Multi-lingual customer support with Salesforce', tools:[{name:'salesforce_crm'},{name:'zendesk_tickets'},{name:'translation_api'}], _demo:true },
  // OpenAI
  { agentId:'AGNT10', name:'openai-code-assistant',    provider:'openai',  model:'gpt-4o',      status:'active', region:'global', description:'Code review, generation and debugging with test execution', tools:[{name:'code_interpreter'},{name:'run_unit_tests'},{name:'get_github_pr'},{name:'file_search'}], _demo:true },
  { agentId:'AGNT11', name:'openai-financial-analyst', provider:'openai',  model:'gpt-4o-mini', status:'active', region:'global', description:'Financial data analysis and chart generation', tools:[{name:'code_interpreter'},{name:'fetch_market_data'},{name:'calculate_metrics'}], _demo:true },
  { agentId:'AGNT12', name:'openai-orchestrator',      provider:'openai',  model:'gpt-4o',      status:'active', region:'global', description:'Multi-agent orchestrator spawning specialist sub-agents', tools:[{name:'spawn_sub_agent'},{name:'wait_for_agent_result'},{name:'merge_agent_outputs'}], isOrchestrator:true, _demo:true },
];

function makeDemoStep(index, type, offset, durationMs, toolName) {
  return { type, index, status:'completed', durationMs, startOffset: offset,
    inTokens: type.includes('message') || type.includes('PROCESSING') ? rndI(200,2000) : 0,
    outTokens: type.includes('RESPONSE') || (index > 0 && index % 3 === 2) ? rndI(100,1200) : 0,
    toolName: toolName || null,
    detail: toolName ? `Called ${toolName}` : `${type.replace(/_/g,' ')} phase`,
  };
}

function makeDemoRun(agent) {
  const statusMap = {
    bedrock:'SUCCEEDED', azure:'completed', vertex:'END_OF_SESSION', openai:'completed',
  };
  const status    = Math.random() < 0.88 ? statusMap[agent.provider] : Math.random() < 0.5 ? 'FAILED' : 'failed';
  const startMs   = Date.now() - rndI(0, 86400000);
  const steps     = [];
  let offset      = 0;

  const preTypes  = { bedrock:'PRE_PROCESSING', azure:'message_creation', vertex:'USER_INPUT',   openai:'message_creation' };
  const postTypes = { bedrock:'POST_PROCESSING', azure:'message_creation', vertex:'AGENT_RESPONSE', openai:'message_creation' };
  const toolTypes = { bedrock:'ACTION_GROUP',   azure:'tool_calls',       vertex:'TOOL_USE',    openai:'tool_calls' };

  const preDur = rndI(100, 600);
  steps.push(makeDemoStep(0, preTypes[agent.provider], 0, preDur, null));
  offset += preDur;

  const numTools = rndI(0, 4);
  for (let i = 0; i < numTools; i++) {
    const tool = agent.tools[rndI(0, agent.tools.length)];
    const d    = rndI(200, 4000);
    steps.push(makeDemoStep(steps.length, toolTypes[agent.provider], offset, d, tool.name));
    offset += d;

    if (Math.random() < 0.4 && agent.provider === 'bedrock') {
      const kbDur = rndI(150, 800);
      steps.push(makeDemoStep(steps.length, 'KNOWLEDGE_BASE_LOOKUP', offset, kbDur, 'KB_Search'));
      offset += kbDur;
    }
  }

  const postDur = rndI(200, 2000);
  steps.push(makeDemoStep(steps.length, postTypes[agent.provider], offset, postDur, null));
  offset += postDur;

  return {
    runId: `run-${Math.random().toString(36).slice(2,14)}`,
    agentId: agent.agentId, agentName: agent.name, provider: agent.provider,
    status, startedAt: new Date(startMs), completedAt: new Date(startMs + offset),
    durationMs: offset, steps,
    inTokens:  steps.reduce((s, st) => s + (st.inTokens||0), 0),
    outTokens: steps.reduce((s, st) => s + (st.outTokens||0), 0),
    toolCalls: steps.filter(s => s.toolName).length,
    model: agent.model, region: agent.region,
    isOrchestrated: !!agent.isOrchestrator,
  };
}

const DEMO_RUNS = (() => {
  const runs = [];
  DEMO_AGENTS.forEach(a => {
    const count = rndI(8, 22);
    for (let i = 0; i < count; i++) runs.push(makeDemoRun(a));
  });
  return runs.sort((a,b) => new Date(b.startedAt) - new Date(a.startedAt));
})();

// Hourly aggregates for charts
const HOURLY = Array.from({ length: 24 }, (_, i) => {
  const bucket = DEMO_RUNS.filter(r => {
    const h = Math.floor((Date.now() - new Date(r.startedAt).getTime()) / 3600000);
    return h === 23 - i;
  });
  const d = new Date(Date.now() - (23 - i) * 3600000);
  const succ = bucket.filter(r => ['SUCCEEDED','completed','END_OF_SESSION'].includes(r.status)).length;
  const fail = bucket.filter(r => ['FAILED','failed'].includes(r.status)).length;
  return {
    hour: d.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }),
    runs: bucket.length, succeeded: succ, failed: fail,
    avgMs: bucket.length ? Math.round(bucket.reduce((s,r)=>s+r.durationMs,0)/bucket.length) : 0,
    tokens: bucket.reduce((s,r)=>s+(r.inTokens||0)+(r.outTokens||0),0),
  };
});

// Tool usage stats
const TOOL_STATS = (() => {
  const map = {};
  DEMO_RUNS.forEach(run => {
    run.steps.filter(s => s.toolName).forEach(s => {
      map[s.toolName] = map[s.toolName] || { name:s.toolName, calls:0, errors:0, totalMs:0, providers:new Set() };
      map[s.toolName].calls++;
      if (s.status === 'failed' || s.status === 'FAILED') map[s.toolName].errors++;
      map[s.toolName].totalMs += s.durationMs||0;
      map[s.toolName].providers.add(run.provider);
    });
  });
  return Object.values(map).map(t => ({
    ...t, providers:[...t.providers],
    avgMs: t.calls ? Math.round(t.totalMs/t.calls) : 0,
    errRate: t.calls ? parseFloat(((t.errors/t.calls)*100).toFixed(1)) : 0,
  })).sort((a,b) => b.calls - a.calls);
})();

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmtMs  = ms  => ms >= 1000 ? `${(ms/1000).toFixed(2)}s` : `${ms}ms`;
const fmtK   = n   => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);
const timeAgo = d  => {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60)   return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  return `${Math.floor(s/3600)}h ago`;
};
const successFor = s => ['SUCCEEDED','completed','END_OF_SESSION'].includes(s);
const failFor    = s => ['FAILED','failed'].includes(s);

function StatusBadge({ s }) {
  const cfg = STATUS_CFG[s] || { color:'#94a3b8', bg:'rgba(148,163,184,.12)', label:s };
  return <span style={{ fontSize:9, fontWeight:700, padding:'1px 7px', borderRadius:20, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.color}30` }}>{cfg.label}</span>;
}

function ProvBadge({ p }) {
  const cfg = PROVIDERS[p] || { label:p, color:'#94a3b8', bg:'rgba(148,163,184,.1)', icon:'●' };
  return (
    <span style={{ fontSize:9, fontWeight:700, padding:'1px 8px', borderRadius:20, background:cfg.bg, color:cfg.color, border:`1px solid ${cfg.border}`, display:'inline-flex', alignItems:'center', gap:3 }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderTop:`3px solid ${color}` }}>
      <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color:'var(--text-3)' }}>
        <Icon size={11} style={{ color }}/>{label}
      </div>
      <div className="mono text-2xl font-bold mb-1" style={{ color }}>{value}</div>
      <div className="text-xs" style={{ color:'var(--text-3)' }}>{sub}</div>
    </div>
  );
}

// ─── Run Detail Slide Panel ───────────────────────────────────────────────────
function RunPanel({ run, onClose }) {
  const prov = PROVIDERS[run.provider] || {};
  const maxDur = Math.max(...run.steps.map(s => (s.startOffset||0) + (s.durationMs||0)));

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:'rgba(0,0,0,.55)', backdropFilter:'blur(3px)' }} onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth:560, background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', boxShadow:'-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom:'1px solid var(--border)', background:`${prov.color}08` }}>
          <div className="text-2xl">{prov.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm">{run.agentName}</span>
              <StatusBadge s={run.status}/>
              {run.isOrchestrated && <span style={{ fontSize:9, padding:'1px 6px', borderRadius:20, background:'rgba(139,92,246,.15)', color:'#c4b5fd', border:'1px solid rgba(139,92,246,.3)', fontWeight:700 }}>ORCHESTRATOR</span>}
            </div>
            <div className="text-xs mt-0.5 mono flex items-center gap-2" style={{ color:'var(--text-3)' }}>
              <span>{run.runId}</span><span>·</span><span>{timeAgo(run.startedAt)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        <div className="p-5 flex flex-col gap-5 overflow-y-auto">
          {/* Metrics grid */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label:'Duration',   value:fmtMs(run.durationMs),  color:'#fbbf24' },
              { label:'In Tokens',  value:fmtK(run.inTokens||0),  color:'#60a5fa' },
              { label:'Out Tokens', value:fmtK(run.outTokens||0), color:'#a78bfa' },
              { label:'Tool Calls', value:run.toolCalls||0,        color:'#fbbf24' },
              { label:'Steps',      value:run.steps.length,        color:'#34d399' },
              { label:'Model',      value:run.model||'—',          color:prov.color||'#94a3b8' },
            ].map(k => (
              <div key={k.label} className="rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>{k.label}</div>
                <div className="text-sm font-bold mono truncate" style={{ color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Execution waterfall */}
          <div>
            <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
              Execution Waterfall — {fmtMs(run.durationMs)} total
            </div>
            <div className="space-y-2">
              {run.steps.map((step, i) => {
                const c     = STEP_COLORS[step.type] || '#94a3b8';
                const pct   = ((step.durationMs||0) / maxDur) * 100;
                const left  = ((step.startOffset||0) / maxDur) * 100;
                const sc    = STATUS_CFG[step.status] || STATUS_CFG['completed'];
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2">
                        <span className="mono" style={{ color:'var(--text-2)' }}>
                          {step.toolName || step.type.replace(/_/g,' ')}
                        </span>
                        {step.toolName && <span style={{ fontSize:9, color:c, fontWeight:700, padding:'0 5px', borderRadius:10, background:`${c}15`, border:`1px solid ${c}25` }}>tool</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="mono" style={{ color:'var(--text-3)' }}>{fmtMs(step.durationMs||0)}</span>
                        {step.status !== 'completed' && step.status !== 'SUCCEEDED' && <StatusBadge s={step.status}/>}
                      </div>
                    </div>
                    <div className="h-5 rounded" style={{ background:'var(--bg-primary)', position:'relative' }}>
                      <div style={{ position:'absolute', left:`${left}%`, width:`${Math.max(pct,1)}%`, height:'100%', background:`${c}80`, borderRadius:4, border:`1px solid ${c}50` }}/>
                    </div>
                    {step.detail && <div className="text-xs mt-0.5" style={{ color:'var(--text-3)', fontSize:9 }}>{step.detail}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Token breakdown */}
          {((run.inTokens||0) + (run.outTokens||0)) > 0 && (
            <div className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>Token Budget</div>
              <div className="flex gap-3">
                {[
                  { label:'Input',  v:run.inTokens||0,  color:'#60a5fa' },
                  { label:'Output', v:run.outTokens||0, color:'#a78bfa' },
                  { label:'Total',  v:(run.inTokens||0)+(run.outTokens||0), color:'#34d399' },
                ].map(t => (
                  <div key={t.label} className="flex-1 text-center rounded-lg py-3" style={{ background:'var(--bg-primary)', border:'1px solid var(--border)' }}>
                    <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>{t.label}</div>
                    <div className="mono font-bold text-lg" style={{ color:t.color }}>{fmtK(t.v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Agent Fleet Detail Panel ─────────────────────────────────────────────────
function AgentFleetPanel({ agent, runs, onClose }) {
  const prov = PROVIDERS[agent.provider] || {};
  const agentRuns = runs.filter(r => r.agentId === agent.agentId);
  const succeeded = agentRuns.filter(r => successFor(r.status)).length;
  const sr = agentRuns.length ? (succeeded / agentRuns.length * 100).toFixed(1) : '0.0';
  const avgMs = agentRuns.length ? Math.round(agentRuns.reduce((s,r)=>s+r.durationMs,0)/agentRuns.length) : 0;
  const [innerRun, setInnerRun] = useState(null);
  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:'rgba(0,0,0,.55)', backdropFilter:'blur(3px)' }} onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth:560, background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', boxShadow:'-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom:'1px solid var(--border)', background:`${prov.color}08` }}>
          <span className="text-2xl">{prov.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm">{agent.name}</span>
              <StatusBadge s={agent.status}/>
              {agent.memoryEnabled && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20, background:'rgba(139,92,246,.15)', color:'#c4b5fd', border:'1px solid rgba(139,92,246,.3)' }}>MEMORY</span>}
              {agent.isOrchestrator && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20, background:'rgba(251,191,36,.15)', color:'#fde68a', border:'1px solid rgba(251,191,36,.3)' }}>ORCHESTRATOR</span>}
            </div>
            <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{prov.label} · {agent.model} · {agent.region}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:18, lineHeight:1 }}>✕</button>
        </div>
        <div className="p-5 flex flex-col gap-5 flex-1 overflow-y-auto">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { l:'Total Runs',   v:agentRuns.length,  c:prov.color },
              { l:'Success Rate', v:`${sr}%`,           c:parseFloat(sr)>=80?'#22c55e':'#f59e0b' },
              { l:'Avg Duration', v:fmtMs(avgMs),       c:'#fbbf24' },
            ].map(k => (
              <div key={k.l} className="rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="mono font-black text-lg" style={{ color:k.c }}>{k.v}</div>
                <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{k.l}</div>
              </div>
            ))}
          </div>
          {/* Description */}
          <div className="rounded-xl p-3 text-xs leading-relaxed" style={{ background:`${prov.color}06`, border:`1px solid ${prov.color}18`, color:'var(--text-2)' }}>
            {agent.description}
          </div>
          {/* Tools */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Tools ({agent.tools?.length || 0})</div>
            <div className="flex flex-wrap gap-1.5">
              {agent.tools?.map(t => (
                <span key={t.name} style={{ fontSize:10, padding:'3px 10px', borderRadius:20, background:prov.bg, color:prov.color, border:`1px solid ${prov.border}`, fontWeight:600 }}>{t.name}</span>
              ))}
            </div>
          </div>
          {/* Run history */}
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Recent Runs ({agentRuns.length})</div>
            {agentRuns.length === 0 ? (
              <div className="text-xs py-4 text-center rounded-lg" style={{ color:'var(--text-3)', background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>No runs recorded for this agent</div>
            ) : (
              <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
                {agentRuns.slice(0, 14).map((r, i) => {
                  const maxDur = Math.max(...r.steps.map(s => (s.startOffset||0)+(s.durationMs||0)), 1);
                  return (
                    <div key={r.runId} className="px-3 py-2.5 cursor-pointer hover:bg-white/[0.03]"
                      style={{ borderBottom:i < Math.min(agentRuns.length-1,13) ? '1px solid rgba(255,255,255,.04)' : 'none' }}
                      onClick={() => setInnerRun(r)}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <StatusBadge s={r.status}/>
                        <span className="mono" style={{ color:'var(--text-3)', fontSize:10 }}>{r.runId.slice(-8)}</span>
                        <span className="mono text-xs ml-auto" style={{ color:'var(--text-3)' }}>{fmtMs(r.durationMs)}</span>
                        <span className="text-xs" style={{ color:'var(--text-3)' }}>{timeAgo(r.startedAt)}</span>
                        <ChevronRight size={11} style={{ color:'var(--text-3)', flexShrink:0 }}/>
                      </div>
                      <div className="flex gap-px h-3 rounded overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                        {r.steps.map((s, j) => {
                          const w = ((s.durationMs||0)/maxDur)*100;
                          const c = STEP_COLORS[s.type] || '#94a3b8';
                          return <div key={j} style={{ width:`${Math.max(w,1)}%`, height:'100%', background:c, opacity:.75 }}/>;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {innerRun && <RunPanel run={innerRun} onClose={() => setInnerRun(null)}/>}
      </div>
    </div>
  );
}

// ─── Tool Detail Panel ────────────────────────────────────────────────────────
function ToolDetailPanel({ tool, runs, onClose }) {
  const callLog = React.useMemo(() => {
    const entries = [];
    runs.forEach(r => {
      r.steps.filter(s => s.toolName === tool.name).forEach(s => {
        entries.push({ prov:r.provider, agentName:r.agentName, status:s.status, ms:s.durationMs||0, ts:r.startedAt });
      });
    });
    return entries.sort((a,b) => new Date(b.ts)-new Date(a.ts)).slice(0,20);
  }, [tool, runs]);
  const hc = tool.errRate < 2 ? '#22c55e' : tool.errRate < 8 ? '#f59e0b' : '#ef4444';
  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:'rgba(0,0,0,.55)', backdropFilter:'blur(3px)' }} onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth:480, background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', boxShadow:'-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 shrink-0" style={{ borderBottom:'1px solid var(--border)' }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background:'rgba(167,139,250,.15)', border:'1px solid rgba(167,139,250,.3)' }}>
            <Wrench size={16} style={{ color:'#a78bfa' }}/>
          </div>
          <div className="flex-1">
            <div className="font-bold font-mono text-sm">{tool.name}</div>
            <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{tool.calls} calls · {tool.providers.length} provider{tool.providers.length>1?'s':''}</div>
          </div>
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 10px', borderRadius:20, background:`${hc}15`, color:hc, border:`1px solid ${hc}30` }}>
            {tool.errRate < 2 ? 'Healthy' : tool.errRate < 8 ? 'Degraded' : 'Unhealthy'}
          </span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', fontSize:18, lineHeight:1 }}>✕</button>
        </div>
        <div className="p-5 flex flex-col gap-5 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {[
              { l:'Total Calls', v:tool.calls,        c:'#60a5fa' },
              { l:'Errors',      v:tool.errors,        c:tool.errors>0?'#f87171':'var(--text-2)' },
              { l:'Error Rate',  v:`${tool.errRate}%`, c:hc },
              { l:'Avg Latency', v:fmtMs(tool.avgMs),  c:tool.avgMs>2000?'#fbbf24':'var(--text-2)' },
            ].map(k => (
              <div key={k.l} className="rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="mono font-black text-lg" style={{ color:k.c }}>{k.v}</div>
                <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{k.l}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
            <div className="flex justify-between text-xs mb-2">
              <span style={{ color:'var(--text-3)' }}>Success / Error split</span>
              <span className="mono font-bold" style={{ color:hc }}>{(100-tool.errRate).toFixed(1)}% success</span>
            </div>
            <div className="flex h-2.5 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
              <div style={{ width:`${100-tool.errRate}%`, height:'100%', background:hc }}/>
              {tool.errRate > 0 && <div style={{ width:`${tool.errRate}%`, height:'100%', background:'#ef4444' }}/>}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Providers Using This Tool</div>
            <div className="flex flex-wrap gap-2">{tool.providers.map(p => <ProvBadge key={p} p={p}/>)}</div>
          </div>
          <div>
            <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.7px' }}>Call History ({callLog.length})</div>
            <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
              {callLog.length === 0 ? (
                <div className="text-xs py-4 text-center" style={{ color:'var(--text-3)' }}>No call history available</div>
              ) : callLog.map((c, i) => {
                const pv = PROVIDERS[c.prov] || {};
                const ok = ['completed','SUCCEEDED','ok','END_OF_SESSION'].includes(c.status);
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs"
                    style={{ borderBottom:i<callLog.length-1?'1px solid rgba(255,255,255,.04)':'none', background:i%2?'rgba(255,255,255,.01)':'transparent' }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:ok?'#22c55e':'#ef4444', flexShrink:0 }}/>
                    <span style={{ color:pv.color }}>{pv.icon} {pv.label}</span>
                    <span className="flex-1 truncate" style={{ color:'var(--text-2)' }}>{c.agentName}</span>
                    <span className="mono" style={{ color:'var(--text-3)' }}>{fmtMs(c.ms)}</span>
                    <span style={{ color:'var(--text-3)' }}>{timeAgo(c.ts)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────
const TABS = ['Fleet', 'Runs', 'Execution', 'Tools', 'Memory'];

export default function AgentCoreView() {
  const [tab,          setTab]          = useState('Fleet');
  const [agents,       setAgents]       = useState(DEMO_AGENTS);
  const [runs,         setRuns]         = useState(DEMO_RUNS);
  const [loading,      setLoading]      = useState(false);
  const [selRun,       setSelRun]       = useState(null);
  const [provFilter,   setProvFilter]   = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search,       setSearch]       = useState('');
  const [expandAgent,  setExpandAgent]  = useState(null);
  const [selAgent,     setSelAgent]     = useState(null);
  const [selTool,      setSelTool]      = useState(null);

  // Try real API on mount
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/agent-obs/agents').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/agent-obs/runs?limit=200').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([agentsRes, runsRes]) => {
      if (agentsRes?.agents?.length) setAgents(agentsRes.agents);
      if (runsRes?.runs?.length)     setRuns(runsRes.runs.map(r => ({ ...r, startedAt: new Date(r.startedAt), completedAt: new Date(r.completedAt) })));
    }).finally(() => setLoading(false));
  }, []);

  const filteredRuns = useMemo(() => runs.filter(r => {
    if (provFilter !== 'ALL'  && r.provider !== provFilter)  return false;
    if (statusFilter !== 'ALL') {
      const ok = statusFilter === 'success' ? successFor(r.status) : statusFilter === 'failed' ? failFor(r.status) : r.status.toLowerCase().includes('progress') || r.status.toLowerCase().includes('running');
      if (!ok) return false;
    }
    if (search && !r.runId.toLowerCase().includes(search) && !r.agentName.toLowerCase().includes(search)) return false;
    return true;
  }), [runs, provFilter, statusFilter, search]);

  // Aggregates
  const totalRuns    = runs.length;
  const succeeded    = runs.filter(r => successFor(r.status)).length;
  const failed       = runs.filter(r => failFor(r.status)).length;
  const avgLatency   = totalRuns ? Math.round(runs.reduce((s,r)=>s+(r.durationMs||0),0)/totalRuns) : 0;
  const totalTokens  = runs.reduce((s,r)=>s+(r.inTokens||0)+(r.outTokens||0),0);
  const totalTools   = runs.reduce((s,r)=>s+(r.toolCalls||0),0);
  const successRate  = totalRuns ? ((succeeded/totalRuns)*100).toFixed(1) : '0.0';

  // Per-provider aggregates
  const byProv = useMemo(() => {
    const m = {};
    for (const p of Object.keys(PROVIDERS)) {
      const pruns = runs.filter(r => r.provider === p);
      const s = pruns.filter(r => successFor(r.status)).length;
      const f = pruns.filter(r => failFor(r.status)).length;
      m[p] = { runs: pruns.length, succeeded: s, failed: f,
        successRate: pruns.length ? ((s/pruns.length)*100).toFixed(1) : '—',
        avgMs: pruns.length ? Math.round(pruns.reduce((sum,r)=>sum+(r.durationMs||0),0)/pruns.length) : 0,
        agents: agents.filter(a => a.provider === p).length,
      };
    }
    return m;
  }, [runs, agents]);

  // Memory/context usage synthetic data
  const memData = DEMO_AGENTS.map(a => ({
    name: a.name.split('-').slice(-1)[0],
    full: a.name,
    provider: a.provider,
    contextUsed: rndI(12, 95),
    longTermEntries: a.memoryEnabled ? rndI(50, 800) : 0,
    vectorDocs: a.name.includes('support') || a.name.includes('research') ? rndI(3000, 30000) : 0,
  }));

  // Execution pattern data
  const execPattern = Array.from({ length: 12 }, (_, i) => {
    const hour = `${String(i * 2).padStart(2,'0')}:00`;
    return {
      hour,
      bedrock: rndI(2, 18), azure: rndI(1, 15), vertex: rndI(1, 12), openai: rndI(2, 20),
    };
  });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background:'linear-gradient(135deg,#f97316,#8b5cf6)', boxShadow:'0 4px 14px rgba(249,115,22,.4)' }}>
          <Network size={16} className="text-white"/>
        </div>
        <div>
          <h1 className="text-base font-bold">AgentCore Observability</h1>
          <p className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>
            Multi-cloud agent fleet · Bedrock · Azure AI · Vertex AI · OpenAI Agents
          </p>
        </div>
        <div className="flex items-center gap-2 ml-4 flex-wrap">
          {Object.entries(PROVIDERS).map(([k,v]) => {
            const stats = byProv[k];
            return (
              <div key={k} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs cursor-pointer"
                style={{ background:v.bg, border:`1px solid ${v.border}`, color:v.color }}
                onClick={() => setProvFilter(provFilter === k ? 'ALL' : k)}>
                <span>{v.icon}</span>
                <span className="font-medium">{v.label}</span>
                <span className="mono font-bold">{stats?.agents || 0} agents</span>
              </div>
            );
          })}
        </div>
        <button onClick={() => window.location.reload()}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
          style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', color:'var(--text-2)', cursor:'pointer' }}>
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''}/>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:'9px 18px', fontSize:12, fontWeight:tab===t?600:400,
              color:tab===t?'var(--text)':'var(--text-3)', background:'none', border:'none', cursor:'pointer',
              borderBottom:tab===t?'2px solid #f97316':'2px solid transparent' }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Fleet ─────────────────────────────────────────────────────────── */}
      {tab === 'Fleet' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* KPI row */}
          <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))' }}>
            <KpiCard icon={Bot}          label="Total Agents"     value={agents.length}            sub={`across ${Object.keys(PROVIDERS).length} providers`} color="#f97316"/>
            <KpiCard icon={Play}         label="Total Runs"       value={fmtK(totalRuns)}           sub="last 24h"                                             color="#60a5fa"/>
            <KpiCard icon={CheckCircle}  label="Success Rate"     value={`${successRate}%`}          sub={`${succeeded} succeeded`}                            color="#22c55e"/>
            <KpiCard icon={XCircle}      label="Failed Runs"      value={failed}                    sub={`${((failed/Math.max(totalRuns,1))*100).toFixed(1)}% failure rate`} color="#ef4444"/>
            <KpiCard icon={Clock}        label="Avg Latency"      value={fmtMs(avgLatency)}          sub="per run"                                             color="#fbbf24"/>
            <KpiCard icon={Wrench}       label="Tool Calls"       value={fmtK(totalTools)}           sub={`${(totalTools/Math.max(totalRuns,1)).toFixed(1)} avg/run`} color="#a78bfa"/>
          </div>

          {/* Provider health cards */}
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(PROVIDERS).map(([k,v]) => {
              const s = byProv[k] || {};
              const agts = agents.filter(a => a.provider === k);
              return (
                <div key={k} className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:`1px solid ${v.border}` }}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">{v.icon}</span>
                    <div>
                      <div className="font-bold text-sm" style={{ color:v.color }}>{v.label}</div>
                      <div className="text-xs" style={{ color:'var(--text-3)' }}>{s.agents||0} agents · {s.runs||0} runs</div>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="mono font-bold" style={{ color:parseFloat(s.successRate)>=80?'#22c55e':parseFloat(s.successRate)>=60?'#f59e0b':'#ef4444' }}>{s.successRate||'—'}%</div>
                      <div className="text-xs" style={{ color:'var(--text-3)' }}>success rate</div>
                    </div>
                  </div>
                  {/* Success bar */}
                  <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background:'var(--bg-primary)' }}>
                    <div style={{ width:`${s.successRate||0}%`, height:'100%', background:v.color, borderRadius:99 }}/>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label:'Runs',    v:s.runs||0,     color:v.color },
                      { label:'Avg ms',  v:fmtMs(s.avgMs||0), color:'#fbbf24' },
                      { label:'Failed',  v:s.failed||0,   color:'#ef4444' },
                    ].map(m => (
                      <div key={m.label} className="text-center rounded-lg py-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                        <div className="mono font-bold text-sm" style={{ color:m.color }}>{m.v}</div>
                        <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                  {/* Agent list */}
                  <div className="mt-3 space-y-1">
                    {agts.slice(0,3).map(a => {
                      const sc = STATUS_CFG[a.status] || STATUS_CFG['active'];
                      return (
                        <div key={a.agentId} className="flex items-center gap-2 rounded px-2 py-1 text-xs cursor-pointer hover:bg-white/[0.03]"
                          style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}
                          onClick={() => setSelAgent(a)}>
                          <span style={{ width:6, height:6, borderRadius:99, background:sc.color, display:'inline-block', flexShrink:0 }}/>
                          <span className="flex-1 truncate" style={{ color:'var(--text-2)' }}>{a.name}</span>
                          <span className="mono" style={{ color:'var(--text-3)', fontSize:9 }}>{a.tools?.length||0} tools</span>
                          <ChevronRight size={9} style={{ color:'var(--text-3)', flexShrink:0 }}/>
                        </div>
                      );
                    })}
                    {agts.length > 3 && <div className="text-xs text-center pt-1" style={{ color:'var(--text-3)' }}>+{agts.length-3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Run volume by provider */}
          <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Agent Execution Volume by Provider (24h)</div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={HOURLY} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <defs>
                  {Object.entries(PROVIDERS).map(([k,v]) => (
                    <linearGradient key={k} id={`af-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={v.color} stopOpacity={0.25}/>
                      <stop offset="100%" stopColor={v.color} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,.04)" vertical={false}/>
                <XAxis dataKey="hour" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false} interval={3}/>
                <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                <Area type="monotone" dataKey="runs" stroke="#f97316" strokeWidth={1.5} fill="url(#af-bedrock)" name="All Runs"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Runs ─────────────────────────────────────────────────────────────── */}
      {tab === 'Runs' && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          {/* Filter bar */}
          <div className="px-5 py-2.5 shrink-0 flex items-center gap-2 flex-wrap"
            style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
            <div className="flex items-center gap-1 rounded px-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
              <Search size={11} style={{ color:'var(--text-3)' }}/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search run ID, agent…"
                className="bg-transparent outline-none text-xs py-1 w-36" style={{ color:'var(--text)' }}/>
            </div>
            {Object.entries(PROVIDERS).map(([k,v]) => (
              <button key={k} onClick={() => setProvFilter(provFilter===k?'ALL':k)}
                className="px-2 py-1 rounded text-xs"
                style={{ background:provFilter===k?v.bg:'var(--bg-tertiary)', color:provFilter===k?v.color:'var(--text-3)',
                  border:`1px solid ${provFilter===k?v.border:'var(--border)'}`, cursor:'pointer' }}>
                {v.icon} {v.label}
              </button>
            ))}
            {['ALL','success','failed','running'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className="px-2 py-1 rounded text-xs"
                style={{ background:statusFilter===s?'rgba(59,130,246,.2)':'var(--bg-tertiary)', color:statusFilter===s?'#60a5fa':'var(--text-3)', border:`1px solid ${statusFilter===s?'rgba(59,130,246,.4)':'var(--border)'}`, cursor:'pointer' }}>
                {s}
              </button>
            ))}
            <span className="ml-auto text-xs" style={{ color:'var(--text-3)' }}>{filteredRuns.length} runs</span>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead style={{ position:'sticky', top:0, zIndex:10 }}>
                <tr style={{ background:'var(--bg-tertiary)' }}>
                  {['Run ID','Agent','Provider','Status','Duration','Tokens','Tools','Steps','Region','When'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:'var(--text-3)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRuns.slice(0,120).map((r, i) => {
                  const pv = PROVIDERS[r.provider] || {};
                  return (
                    <tr key={r.runId}
                      onClick={() => setSelRun(r)}
                      className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                      style={{ borderBottom:'1px solid rgba(255,255,255,.04)', background:i%2?'rgba(255,255,255,.01)':'transparent' }}>
                      <td style={{ padding:'9px 12px' }}><span className="mono text-xs font-bold" style={{ color:'var(--text-3)', fontSize:10 }}>{r.runId.slice(-10)}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="text-xs font-medium" style={{ color:pv.color }}>{r.agentName}</span></td>
                      <td style={{ padding:'9px 12px' }}><ProvBadge p={r.provider}/></td>
                      <td style={{ padding:'9px 12px' }}><StatusBadge s={r.status}/></td>
                      <td style={{ padding:'9px 12px' }}><span className="mono text-xs" style={{ color:r.durationMs>8000?'#f87171':r.durationMs>3000?'#fbbf24':'var(--text-2)' }}>{fmtMs(r.durationMs||0)}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="mono text-xs" style={{ color:'#a78bfa' }}>{fmtK((r.inTokens||0)+(r.outTokens||0))}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="mono text-xs" style={{ color:'#fbbf24' }}>{r.toolCalls||0}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="mono text-xs" style={{ color:'var(--text-3)' }}>{r.steps?.length||0}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="text-xs" style={{ color:'var(--text-3)' }}>{r.region||'—'}</span></td>
                      <td style={{ padding:'9px 12px' }}><span className="text-xs" style={{ color:'var(--text-3)' }}>{timeAgo(r.startedAt)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Execution ──────────────────────────────────────────────────────────── */}
      {tab === 'Execution' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Step type distribution */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Step Type Distribution</div>
              {(() => {
                const counts = {};
                DEMO_RUNS.forEach(r => r.steps.forEach(s => { counts[s.type] = (counts[s.type]||0)+1; }));
                const total  = Object.values(counts).reduce((a,b)=>a+b,0);
                return Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([type, cnt]) => {
                  const c = STEP_COLORS[type] || '#94a3b8';
                  return (
                    <div key={type} className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span style={{ color:'var(--text-2)' }}>{type.replace(/_/g,' ')}</span>
                        <span className="mono font-bold" style={{ color:c }}>{cnt}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                        <div style={{ width:`${(cnt/total)*100}%`, height:'100%', background:c, borderRadius:99 }}/>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Execution Heatmap by Provider (2h buckets)</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={execPattern} barCategoryGap="20%" margin={{ top:0, right:0, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="rgba(255,255,255,.04)"/>
                  <XAxis dataKey="hour" tick={{ fill:'#64748b', fontSize:8 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:'#64748b', fontSize:8 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                  {Object.entries(PROVIDERS).map(([k,v]) => (
                    <Bar key={k} dataKey={k} name={v.label} fill={`${v.color}90`} stackId="a" radius={[0,0,0,0]}/>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Latency percentiles per provider */}
          <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Latency Percentiles by Provider</div>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(PROVIDERS).map(([k,v]) => {
                const pruns = DEMO_RUNS.filter(r => r.provider === k).sort((a,b) => a.durationMs - b.durationMs);
                const pctile = pct => pruns[Math.floor(pruns.length * pct)]?.durationMs || 0;
                return (
                  <div key={k} className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:`1px solid ${v.border}` }}>
                    <div className="flex items-center gap-1.5 mb-3">
                      <span>{v.icon}</span>
                      <span className="text-xs font-semibold" style={{ color:v.color }}>{v.label}</span>
                    </div>
                    {[['p50',0.5],['p75',0.75],['p90',0.90],['p99',0.99]].map(([label,p]) => (
                      <div key={label} className="flex justify-between text-xs mb-1.5">
                        <span style={{ color:'var(--text-3)' }}>{label}</span>
                        <span className="mono font-bold" style={{ color:p>=0.90?'#f87171':p>=0.75?'#fbbf24':v.color }}>{fmtMs(pctile(p))}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent runs mini-list */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Latest Executions — click to inspect steps</span>
            </div>
            {DEMO_RUNS.slice(0, 12).map((r, i) => {
              const pv = PROVIDERS[r.provider] || {};
              const maxDur = Math.max(...r.steps.map(s => (s.startOffset||0) + (s.durationMs||0)), 1);
              return (
                <div key={r.runId}
                  className="px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
                  style={{ borderBottom:i<11?'1px solid rgba(255,255,255,.04)':'none' }}
                  onClick={() => setSelRun(r)}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm">{pv.icon}</span>
                    <span className="text-xs font-semibold" style={{ color:pv.color }}>{r.agentName}</span>
                    <StatusBadge s={r.status}/>
                    <span className="mono text-xs ml-auto" style={{ color:'var(--text-3)' }}>{fmtMs(r.durationMs||0)}</span>
                  </div>
                  {/* Mini waterfall */}
                  <div className="flex gap-0.5 h-4">
                    {r.steps.map((s, j) => {
                      const w = ((s.durationMs||0)/maxDur)*100;
                      const c = STEP_COLORS[s.type] || '#94a3b8';
                      return (
                        <div key={j} title={`${s.type}: ${fmtMs(s.durationMs||0)}`}
                          style={{ width:`${Math.max(w,1)}%`, height:'100%', background:c, borderRadius:2, opacity:0.75 }}/>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tools ─────────────────────────────────────────────────────────────── */}
      {tab === 'Tools' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Tool KPIs */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label:'Registered Tools',  value:TOOL_STATS.length,                          color:'#60a5fa' },
              { label:'Total Calls',       value:fmtK(TOOL_STATS.reduce((s,t)=>s+t.calls,0)),color:'#a78bfa' },
              { label:'Avg Latency',       value:fmtMs(Math.round(TOOL_STATS.reduce((s,t)=>s+t.avgMs,0)/Math.max(TOOL_STATS.length,1))), color:'#fbbf24' },
              { label:'Overall Error Rate',value:`${(TOOL_STATS.reduce((s,t)=>s+t.errors,0)/Math.max(TOOL_STATS.reduce((s,t)=>s+t.calls,0),1)*100).toFixed(1)}%`, color:'#ef4444' },
            ].map(k => {
              const Icon = Wrench;
              return (
                <div key={k.label} className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderTop:`3px solid ${k.color}` }}>
                  <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color:'var(--text-3)' }}>
                    <Icon size={11} style={{ color:k.color }}/>{k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color:k.color }}>{k.value}</div>
                </div>
              );
            })}
          </div>

          {/* Top tools chart */}
          <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Top 12 Tools by Call Count</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={TOOL_STATS.slice(0,12)} layout="vertical" barSize={12} margin={{ top:0, right:10, left:60, bottom:0 }}>
                <XAxis type="number" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                <YAxis type="category" dataKey="name" tick={{ fill:'#94a3b8', fontSize:9 }} axisLine={false} tickLine={false} width={120}/>
                <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                <Bar dataKey="calls" name="Calls" radius={[0,4,4,0]}>
                  {TOOL_STATS.slice(0,12).map((t,i) => <Cell key={i} fill={`hsl(${250 - i*15},70%,65%)`}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tool table */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Tool Registry & Performance</span>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'var(--bg-tertiary)' }}>
                  {['Tool','Providers','Total Calls','Errors','Error Rate','Avg Latency','Health'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:'var(--text-3)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOOL_STATS.map((t, i) => {
                  const health = t.errRate < 2 ? 'Good' : t.errRate < 8 ? 'Fair' : 'Poor';
                  const hc     = t.errRate < 2 ? '#22c55e' : t.errRate < 8 ? '#f59e0b' : '#ef4444';
                  return (
                    <tr key={t.name} onClick={() => setSelTool(t)} className="cursor-pointer hover:bg-white/[0.03] transition-colors" style={{ borderBottom:'1px solid rgba(255,255,255,.04)', background:i%2?'rgba(255,255,255,.01)':'transparent' }}>
                      <td style={{ padding:'10px 12px' }}>
                        <div className="flex items-center gap-1.5">
                          <Wrench size={11} style={{ color:'#a78bfa' }}/>
                          <span className="font-mono text-xs font-semibold" style={{ color:'var(--text-2)' }}>{t.name}</span>
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px' }}>
                        <div className="flex gap-1 flex-wrap">
                          {t.providers.map(p => <ProvBadge key={p} p={p}/>)}
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px' }}><span className="mono font-bold" style={{ color:'#60a5fa' }}>{t.calls}</span></td>
                      <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:t.errors>0?'#f87171':'var(--text-3)' }}>{t.errors}</span></td>
                      <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:t.errRate>5?'#f87171':t.errRate>2?'#fbbf24':'#4ade80' }}>{t.errRate}%</span></td>
                      <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:t.avgMs>2000?'#f87171':t.avgMs>800?'#fbbf24':'var(--text-2)' }}>{fmtMs(t.avgMs)}</span></td>
                      <td style={{ padding:'10px 12px' }}>
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:`${hc}18`, color:hc, border:`1px solid ${hc}30` }}>{health}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Memory ────────────────────────────────────────────────────────────── */}
      {tab === 'Memory' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          <div className="rounded-xl p-4 flex items-start gap-2"
            style={{ background:'rgba(139,92,246,.06)', border:'1px solid rgba(139,92,246,.2)' }}>
            <Info size={13} style={{ color:'#c4b5fd', marginTop:1, flexShrink:0 }}/>
            <p className="text-xs leading-relaxed" style={{ color:'#c4b5fd' }}>
              Context window utilization, long-term memory entries, and vector store retrieval stats for each registered agent.
              Agents approaching context limits will be flagged. Memory-enabled agents (Bedrock, OpenAI) show persisted session state.
            </p>
          </div>

          {/* Context utilization grid */}
          <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))' }}>
            {DEMO_AGENTS.map(agent => {
              const pv   = PROVIDERS[agent.provider] || {};
              const mem  = memData.find(m => m.full === agent.name) || {};
              const used = mem.contextUsed || rndI(15, 90);
              const uc   = used > 80 ? '#ef4444' : used > 60 ? '#f59e0b' : '#22c55e';
              return (
                <div key={agent.agentId} className="rounded-xl p-4 cursor-pointer hover:border-white/[0.12] transition-all" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}
                  onClick={() => setSelAgent(agent)}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">{pv.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate" style={{ color:pv.color }}>{agent.name}</div>
                      <div className="text-xs" style={{ color:'var(--text-3)' }}>{pv.label} · {agent.model}</div>
                    </div>
                    <StatusBadge s={agent.status}/>
                  </div>

                  {/* Context window */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color:'var(--text-3)' }}>Context Window</span>
                      <span className="mono font-bold" style={{ color:uc }}>{used}% used</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                      <div style={{ width:`${used}%`, height:'100%', background:uc, borderRadius:99, transition:'width .5s' }}/>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg py-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                      <div className="mono font-bold text-sm" style={{ color:agent.memoryEnabled?'#34d399':'var(--text-3)' }}>
                        {agent.memoryEnabled ? fmtK(mem.longTermEntries||rndI(50,800)) : '—'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color:'var(--text-3)', fontSize:9 }}>Memory</div>
                    </div>
                    <div className="rounded-lg py-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                      <div className="mono font-bold text-sm" style={{ color:'#a78bfa' }}>
                        {agent.tools?.length || 0}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color:'var(--text-3)', fontSize:9 }}>Tools</div>
                    </div>
                    <div className="rounded-lg py-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                      <div className="mono font-bold text-sm" style={{ color:'#60a5fa' }}>
                        {mem.vectorDocs > 0 ? fmtK(mem.vectorDocs) : '—'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color:'var(--text-3)', fontSize:9 }}>KB Docs</div>
                    </div>
                  </div>

                  {/* Tool list */}
                  <div className="mt-3 flex flex-wrap gap-1">
                    {agent.tools?.slice(0,5).map((t, idx) => (
                      <span key={idx} style={{ fontSize:9, padding:'1px 7px', borderRadius:20, background:pv.bg, color:pv.color, border:`1px solid ${pv.border}` }}>
                        {t.name}
                      </span>
                    ))}
                    {(agent.tools?.length||0) > 5 && <span style={{ fontSize:9, color:'var(--text-3)' }}>+{agent.tools.length-5}</span>}
                  </div>

                  {used > 75 && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs rounded-lg px-2 py-1.5"
                      style={{ background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', color:'#fca5a5' }}>
                      <AlertTriangle size={10}/>
                      Context window {used > 85 ? 'critical' : 'warning'} — consider summarization
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Run detail panel */}
      {selRun   && <RunPanel        run={selRun}   onClose={() => setSelRun(null)}/>}
      {selAgent && <AgentFleetPanel agent={selAgent} runs={runs} onClose={() => setSelAgent(null)}/>}
      {selTool  && <ToolDetailPanel tool={selTool}  runs={runs} onClose={() => setSelTool(null)}/>}
    </div>
  );
}
